import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import {
  Table,
  Select,
  Input,
  Button,
  Popconfirm,
  Tag,
  Avatar
} from 'antd';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faCogs, faSignOut, faUser } from '@fortawesome/pro-duotone-svg-icons';
import { notificationsAtom, userAtom, userPrivilagesAtom } from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { Spinner } from '../../../shared-components/Spinner';
import { H1, H4 } from '../../../shared-components/typography/Title';
import Form from '../../../shared-components/form/Form';
import CustomInput from '../../../shared-components/form/Input';
import api from '../../../../api/api';
import { mapLeadStatus, thousand, toFormattedLocalTime } from '../../../../functions';
import envConfig from '../../../../envConfig';

function AdminRequests() {
  const { t, i18n } = useTranslation();
  const { Option } = Select;
  const searchTypes = [
    { name: t('pending'), type: 0 },
    { name: t('approved'), type: 1 },
    { name: t('rejected'), type: 2 },
    { name: t('all'), type: 3 }
  ];
  const profile = useRecoilValue(userAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedSearchType, setSelectedSearchType] = useState(1);

  function search(value, page) {
    setLastSearchTerm(value);
    setCurrentPage(page);
    setLoading(true);
    api
      .post(
        'leads/requests',
        {
          Query: value,
          Size: pageSize,
          Page: page,
          Filter: selectedSearchType
        }
      )
      .then((response) => {
        setLoading(false);
        if (response?.data?.success) {
          setSearchResult(response.data.data);
        } else {
          setSearchResult({
            ...searchResult,
            users: []
          });
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function onSearch(value) {
    search(value, 0);
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      search(lastSearchTerm, page - 1);
    } else {
      setPageSize(size);
    }
  };

  function updateRequest(lead, approved) {
    let requests = [...searchResult?.requests];
    for (let i = 0; i < requests.length; i++) {
      if (requests[i].id === lead.id) {
        requests[i].loading = true; 
      }
    }
    setSearchResult({
      ...searchResult,
      requests
    });
    api
      .post(
        'leads/request/update',
        {
          LeadRequestId: lead.id,
          Approved: approved
        }
      )
      .then((response) => {
        requests = [...searchResult?.requests];
        for (let i = 0; i < requests.length; i++) {
          if (requests[i].id === lead.id) {
            requests[i].loading = false; 
          }
        }
        setSearchResult({
          ...searchResult,
          requests
        });
        if (response.data.message) {
          setNotifications([
            ...notifications,
            {
              title: t('success'),
              description: response.data.message,
              error: false,
            },
          ]);
        }
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        }
        search(lastSearchTerm, currentPage);
      })
      .catch((error) => {
        requests = [...searchResult?.requests];
        for (let i = 0; i < requests.length; i++) {
          if (requests[i].id === lead.id) {
            requests[i].loading = false; 
          }
        }
        setSearchResult({
          ...searchResult,
          requests
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  useEffect(() => {
    search(lastSearchTerm, 0);
  }, [selectedSearchType, pageSize]);

  useEffect(() => {
    // getOrganizations();
    search('', 0);
  }, []);

  return (
    <MainPage includeBack requiresCloser>
      <div className="flex mt-4">
        <H1 text={t('requests')} />
        {/* <div className="flex ml-auto space-x-2">
          <Button
            key="reload"
            size="small"
            type="primary"
            onClick={() => {
              search(lastSearchTerm, 0);
            }}
          >
            {t('reload')}
          </Button>
        </div> */}
      </div>
      <div className="lg:flex items-center justify-center">
        <Input.Search
          placeholder={`${t('search')}...`}
          allowClear
          className="mt-4"
          enterButton={t('search')}
          size="large"
          onSearch={(val) => onSearch(val)}
        />
        <div className="grid grid-cols-2 md:space-x-2 lg:flex">
          <Select
            className="h-10 col-span-2 mt-2 md:col-span-1 lg:mt-4 lg:w-36 lg:ml-2"
            value={selectedSearchType}
            onChange={(value) => setSelectedSearchType(value)}
          >
            {_.map(searchTypes, (filter) => (
              <Option
                key={filter.type}
                value={filter.type}
                className="text-sm flex items-center"
              >
                {filter.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="mt-4">{t('showing')}: {searchResult?.total ?? 0}</div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: searchResult?.total,
          onChange: onTableChange
        }}
        dataSource={searchResult?.requests}
        columns={[
          {
            title: t('created_on'),
            align: 'center',
            className: 'text-sm',
            render: (row) => (
              <span>{row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
            ),
          },
          {
            title: t('requested_by'),
            align: 'center',
            className: 'text-sm',
            render: (row) => (
              <span>
                <Avatar src={row?.requestor?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                {row?.requestor?.completeName}
              </span>
            ),
          },
          {
            title: t('client'),
            align: 'center',
            className: 'text-sm',
            render: (row) => (
              <span>
                <Avatar src={row?.submission?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                {row?.submission?.submittedBy?.completeName}
              </span>
            ),
          },
          {
            title: t('status'),
            align: 'center',
            className: 'text-sm',
            render: (row) => (
              <>
                {row.status === 0 && (
                  <div className="space-x-2">
                    <Popconfirm
                      title={t('confirm_reject_request_prompt')}
                      okText={t('yes')}
                      cancelText={t('no')}
                      onConfirm={() => updateRequest(row, false)}
                    >
                      <Button loading={row.loading} danger type="primary" size="small">{t('reject')}</Button>
                    </Popconfirm>
                    <Popconfirm
                      title={t('confirm_approve_request_prompt')}
                      okText={t('yes')}
                      cancelText={t('no')}
                      onConfirm={() => updateRequest(row, true)}
                    >
                      <Button loading={row.loading} type="primary" size="small">{t('approve')}</Button>
                    </Popconfirm>
                  </div>
                )}
                {row.status === 1 && (
                  <Tag color="green" size="small">{t('approved')}</Tag>
                )}
                {row.status === 2 && (
                  <Tag color="red" size="small">{t('rejected')}</Tag>
                )}
              </>
            ),
          },
        ]}
      />
    </MainPage>
  );
}

export default AdminRequests;
